import { CommandType } from "@buildwithflux/models";
import { cloneDeep, noop } from "lodash";
import { createDocumentMutator } from "../mutators";
import { DocumentSubscriptionManager } from "./DocumentSubscriptionManager";
const INITIAL_SNAPSHOT_PLACEHOLDER = {
    uid: "",
    name: "",
    description: "",
    pcbLayoutNodes: {},
    pcbLayoutRuleSets: {},
};
export class DocumentService {
    unitOfWorkCreator;
    // TODO: Start implementing this we implement a boarder scope of data and APIs
    // in DocumentService. Then we can remove the `_pcbNodes` field because thats part
    // of `IDocumentData`
    // private document: IDocumentData | null;
    /**
     * This field holds the latest document state
     */
    document;
    /**
     * This field holds a initial snapshot of the document state, it will
     * be used to perform a rebase, by applying events to this snapshot
     */
    initialSnapshot;
    /**
     * When an update is performed, some processing time is needed to apply the baking changes.
     * During that time, the data in the documentService is stale. This flag is used to check that status.
     */
    isDataStale = false;
    commands = [];
    subscriptionManager;
    /**
     * Mutates any snapshot of document by applying a command, and returns a new docuemnt state:
     *
     * documentState + command = newDocumentState
     */
    documentMutator;
    /**
     *
     * @param unitOfWorkCreator - creator to create a ChangeCaptureUnitOfWork
     */
    constructor(unitOfWorkCreator) {
        this.unitOfWorkCreator = unitOfWorkCreator;
        // Initializing both the very first snapshot and the document state.
        // Both will get overwritten once the actual document data is loaded, here is more of a placeholder
        this.initialSnapshot = INITIAL_SNAPSHOT_PLACEHOLDER;
        this.document = cloneDeep(this.initialSnapshot);
        this.subscriptionManager = new DocumentSubscriptionManager();
        this.documentMutator = createDocumentMutator();
    }
    /**
     * Initialize the `DocumentService` instance by
     * - Setup initial snapshot and load `documentData` from server
     * - Setup subscriptions to document events
     */
    onDocumentLoad(documentData) {
        // Stores an initial snapshot so that we have a snapshot to rebase from
        this.initialSnapshot = {
            uid: documentData.uid,
            name: documentData.name,
            description: documentData.description,
            // not loading node and rule data here because that will be loaded after baking
            pcbLayoutNodes: {},
            pcbLayoutRuleSets: {},
        };
        this.document = cloneDeep(this.initialSnapshot);
        // Both local commands and history are empty for a newly loaded document
        this.commands = [];
    }
    /** @inheritDoc */
    snapshot() {
        return this.document;
    }
    /**
     * @inheritDoc
     * @param immediate - if true, the document will be cleared immediately. If false, the document will be cleared
     *     when the returned callback is called. That is useful when we need to externally control when document is
     *     cleared, and notification of clearing is omitted to the reset of the application
     *
     * Note: it only returns a callback with side-effect, when immediate is set to false. If immediate is set to true,
     *     a noop callback is returned
     */
    clear(immediate = true) {
        this.isDataStale = false;
        if (!this.document) {
            return noop;
        }
        const clearDocumentCommand = {
            type: CommandType.clearDocumentCommand,
        };
        if (immediate) {
            this.applyCommand(clearDocumentCommand);
            return noop;
        }
        else {
            return clearDocumentCommand;
        }
    }
    /**
     * Use this to subscribe to {@link DocumentChange} as an outcome of document mutation
     *
     * Caller should use the return `Unsubscriber` method to clean up the subscription properly
     */
    subscribe(params) {
        // Delegate the subscription to `subscriptionManager`
        return this.subscriptionManager.subscribe(params);
    }
    /**
     * Apply a passed in LOCAL command to mutate document state, and notify downstream modules
     * about the mutation
     */
    applyCommand(command) {
        if (!this.document)
            return;
        const uow = this.applyCommandInternal(this.document, command);
        // If there's any "ChangeCapture" unit of work returned, commit it
        if (uow) {
            uow.commit(this.subscriptionManager.notify);
        }
    }
    setDataAsStale(stale) {
        this.isDataStale = stale;
    }
    getIsDataStale() {
        return this.isDataStale;
    }
    /**
     * Internal function apply the command, which does the following jobs:
     * 1. Record the command if its from local
     * 2. Mutate document state by applying it (optimistic update)
     * 3. Build a unit of work for change capture, and will return it to the caller, let the caller
     *    decide when to commit the unit of work (i.e. commit all the changes)
     * 4. If its a local command, ask `commandPersistenceService` to persist it to the backend
     *
     * The difference between this internal one and the public `applyCommand` is that this one is called
     * to handled both local commands and commands from remote events, whereas the public one is only
     * called to handle local command
     */
    applyCommandInternal(snapshot, command, isRemote) {
        // Record command if its from local
        if (!isRemote)
            this.commands.push(command);
        // Mutate document state by mutator, and pass in a uow, let mutator add changes to it if needed
        const uow = this.unitOfWorkCreator();
        const captureChangeUnitOfWorkOptions = {
            uow,
            getListeners: this.subscriptionManager.getListeners.bind(this.subscriptionManager),
        };
        this.document = this.documentMutator.applyCommand(snapshot, command, captureChangeUnitOfWorkOptions);
        // Return the possible unit of work and let caller to decide when to commit it
        return uow;
    }
}
//# sourceMappingURL=DocumentService.js.map